import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppWindow, ClipboardList, Eye, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Section, dateFr } from './shared';
import { ouvrirPdf } from '../reglement-interieur/SignerReglementDialog';
import { DateInputFR } from '../DateInputFR';

const erreur = (e, msg) => toast.error(e.response?.data?.detail || msg);
const CONSTATS = [
  ['conforme', 'Conforme', 'bg-emerald-100 text-emerald-800'],
  ['point_fort', 'Point fort', 'bg-sky-100 text-sky-800'],
  ['piste', "Piste d'amélioration", 'bg-amber-100 text-amber-800'],
  ['nc_mineure', 'Non-conformité mineure', 'bg-orange-100 text-orange-800'],
  ['nc_majeure', 'Non-conformité majeure', 'bg-red-100 text-red-800'],
];
const couleur = (c) => (CONSTATS.find(x => x[0] === c) || [])[2] || 'bg-slate-100 text-slate-700';

const Catalogue = ({ num, dejaLa, onUpdate, onFermer }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => { axios.get('/qualiopi/catalogue-appli').then(r => setItems(r.data)).catch(e => erreur(e, 'Catalogue indisponible')); }, []);
  const liste = (items || []).filter(i => !dejaLa.has(i.url) && `${i.categorie} ${i.titre}`.toLowerCase().includes(q.toLowerCase()));
  const ajouter = async (i) => {
    setBusy(i.url);
    try { onUpdate((await axios.post(`/qualiopi/indicateurs/${num}/elements-appli`, i)).data); toast.success('Élément ajouté'); onFermer(); }
    catch (e) { erreur(e, "Ajout impossible"); } finally { setBusy(''); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="qualiopi-catalogue">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Livret, règlement, PIF, devis, nom…" className="pl-9" data-testid="qualiopi-catalogue-recherche" />
        </div>
        <Button size="sm" variant="ghost" className="h-10" onClick={onFermer} data-testid="qualiopi-catalogue-fermer">Fermer</Button>
      </div>
      {!items ? <p className="text-sm text-slate-400"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Chargement…</p> : (
        <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
          {liste.map(i => (
            <li key={i.url} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="w-28 shrink-0 text-xs text-slate-400">{i.categorie}</span>
              <span className="flex-1 text-slate-700">{i.titre}</span>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!!busy} onClick={() => ajouter(i)} data-testid={`qualiopi-catalogue-ajouter-${i.url}`}>
                {busy === i.url ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Ajouter
              </Button>
            </li>
          ))}
          {liste.length === 0 && <li className="py-2 text-sm text-slate-400">Aucun élément</li>}
        </ul>
      )}
    </div>
  );
};

export const ElementsAppliSection = ({ num, elements, onUpdate }) => {
  const [ouvert, setOuvert] = useState(false);
  const retirer = async (id) => {
    try { onUpdate((await axios.delete(`/qualiopi/indicateurs/${num}/elements-appli/${id}`)).data); } catch (e) { erreur(e, 'Suppression impossible'); }
  };
  return (
    <Section titre="Éléments de l'appli ajoutés" icone={AppWindow} compteur={elements.length} testId="qualiopi-elements-appli">
      {elements.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {elements.map(e => (
            <li key={e.element_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`qualiopi-element-${e.element_id}`}>
              <span className="w-28 shrink-0 text-xs text-slate-400">{e.categorie}</span>
              <span className="flex-1 text-slate-700">{e.titre}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" title="Voir" onClick={() => ouvrirPdf(e.url, `${e.titre}.pdf`)} data-testid={`qualiopi-element-voir-${e.element_id}`}><Eye className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => retirer(e.element_id)} data-testid={`qualiopi-element-retirer-${e.element_id}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
      {ouvert ? <Catalogue num={num} dejaLa={new Set(elements.map(e => e.url))} onUpdate={onUpdate} onFermer={() => setOuvert(false)} /> : (
        <Button size="sm" variant="outline" onClick={() => setOuvert(true)} data-testid="qualiopi-ajouter-depuis-appli"><Plus className="h-4 w-4 mr-1" /> Ajouter depuis l'appli</Button>
      )}
    </Section>
  );
};

export const NotesAuditSection = ({ num, notes, onUpdate }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), constat: 'conforme', note: '' });
  const [busy, setBusy] = useState(false);
  const ajouter = async () => {
    setBusy(true);
    try { onUpdate((await axios.post(`/qualiopi/indicateurs/${num}/notes-audit`, form)).data); setForm({ ...form, note: '' }); toast.success("Note d'audit enregistrée"); }
    catch (e) { erreur(e, 'Enregistrement impossible'); } finally { setBusy(false); }
  };
  const supprimer = async (id) => {
    if (!window.confirm("Supprimer définitivement cette note d'audit ?")) return;
    try { onUpdate((await axios.delete(`/qualiopi/indicateurs/${num}/notes-audit/${id}`)).data); } catch (e) { erreur(e, 'Suppression impossible'); }
  };
  return (
    <Section titre="Notes d'audit (conservées d'un audit à l'autre)" icone={ClipboardList} compteur={notes.length} testId="qualiopi-notes-audit">
      <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap gap-2">
          <DateInputFR value={form.date} onChange={(v) => setForm({ ...form, date: v?.target ? v.target.value : v })} className="h-9 w-40 bg-white" data-testid="qualiopi-note-date" />
          <select value={form.constat} onChange={e => setForm({ ...form, constat: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="qualiopi-note-constat">
            {CONSTATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <Textarea rows={3} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="bg-white"
          placeholder="Ce qu'a dit ou demandé l'auditeur, ce qu'il faudra montrer la prochaine fois…" data-testid="qualiopi-note-texte" />
        <Button size="sm" onClick={ajouter} disabled={busy || !form.note.trim() || !form.date} data-testid="qualiopi-note-ajouter">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Ajouter la note
        </Button>
      </div>
      {notes.length === 0 ? <p className="text-sm text-slate-400">Aucune note d'audit pour cet indicateur.</p> : (
        <ul className="space-y-2">
          {notes.map(n => (
            <li key={n.note_id} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`qualiopi-note-${n.note_id}`}>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-800">Audit du {dateFr(n.date)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${couleur(n.constat)}`}>{n.constat_label}</span>
                <span className="flex-1 text-right text-xs text-slate-400">par {n.par_nom}</span>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-red-600" onClick={() => supprimer(n.note_id)} data-testid={`qualiopi-note-supprimer-${n.note_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
              <p className="whitespace-pre-wrap text-slate-700">{n.note}</p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};
